import {Class} from "../../../../../../lib/legacyApi/fetchClassesForCourseByCampusId";
import ClassSchedule from "./ClassSchedule";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCirclePlus} from "@fortawesome/sharp-solid-svg-icons/faCirclePlus";
import Button, {ButtonVariant} from "../../../../../_components/Button";
import React, {useState} from "react";
import StudentRadioGroup from "./StudentRadioGroup";
import ConsultationRequiredModalBody from "./ConsultationRequiredModalBody";
import NotLoggedInModalBody from "./NotLoggedInModalBody";
import getLegacySitePath from "../../../../../util/getLegacySitePath";
import LinkButton from "@/app/_components/LinkButton";
import EnrollStatus from "@/app/types/EnrollStatus";
import {StudentForEnrollment} from "@/shared/legacyTypes/fetchEnrollmentDataTypes";

interface EnrollModalBodyProps {
	class: Class;
	close: () => void;
	students: StudentForEnrollment[];
	isLoggedIn: boolean;
	isConsultationRequired: boolean;
	isSummer: boolean;
}

/**
 * Make the href used by the "Add another student" link.
 *
 * The query params depend on the class's enroll status.
 *
 * @param classToAdd The class to add a student to
 */
const makeAddNewStudentHref = (classToAdd: Class) => {
	const registrationParams = new URLSearchParams();
	if (classToAdd.enrollStatus === EnrollStatus.WAITLIST) {
		registrationParams.set("waitlist", classToAdd.classId);
	} else if (classToAdd.enrollStatus === EnrollStatus.OPEN) {
		registrationParams.set("class", classToAdd.classId);
	}
	const addNewStudentHref = getLegacySitePath(
		`/admissions/registration?${registrationParams}`,
		classToAdd.campusId
	);
	return addNewStudentHref;
};

/**
 * The modal body for enrolling in a class from the course page.
 *
 * Loading the data and opening the modal are controlled by AddToCartButton.
 */
const EnrollModalBody: React.FC<EnrollModalBodyProps> = ({
	class: classToAdd,
	close,
	students,
	isLoggedIn,
	isConsultationRequired,
	isSummer,
}) => {
	const [selectedStudent, setSelectedStudent] = useState<string | undefined>(
		undefined
	);
	const [
		isConsultationRequiredBodyTriggered,
		setIsConsultationRequiredBodyTriggered,
	] = useState<boolean>(false);
	if (!isLoggedIn) {
		return (
			<NotLoggedInModalBody
				class={classToAdd}
				isConsultationRequired={isConsultationRequired}
			/>
		);
	} else if (isConsultationRequired || isConsultationRequiredBodyTriggered) {
		return <ConsultationRequiredModalBody />;
	}
	const addNewStudentHref = makeAddNewStudentHref(classToAdd);
	const enrollStudentHref = getLegacySitePath(
		`/courses/enroll?cid=${classToAdd.classId}&student_platform_id=${selectedStudent}`,
		classToAdd.campusId
	);
	return (
		<div className="flex max-w-lg flex-col gap-6 py-12 lg:min-w-[30rem]">
			<h2 className="px-12">{classToAdd.name}</h2>
			<div className="bg-gray-100 px-12 py-4">
				<ClassSchedule
					className="flex flex-col gap-2"
					class={classToAdd}
					showDayOfWeek
				/>
			</div>
			<div className="flex flex-col gap-6 px-12">
				<div className="flex flex-col gap-2.5">
					<StudentRadioGroup
						selectedStudent={selectedStudent}
						setSelectedStudent={setSelectedStudent}
						students={students}
					/>
					<a
						className="flex cursor-pointer items-center gap-2 text-hyperlink"
						onClick={() => {
							if (!isSummer) {
								setIsConsultationRequiredBodyTriggered(true);
							} else {
								window.location.href = addNewStudentHref;
							}
						}}
					>
						<FontAwesomeIcon icon={faCirclePlus} /> Add{" "}
						{students.length ? "another" : "a"} student
					</a>
				</div>
				<div className="flex w-full flex-col gap-2 xs:flex-row">
					<Button
						id="enroll-modal-cancel-button"
						variant={ButtonVariant.GRAY_SECONDARY}
						className="grow"
						onClick={close}
					>
						CANCEL
					</Button>
					{selectedStudent ? (
						<LinkButton
							className="add-to-cart-submit-button grow"
							variant={ButtonVariant.NAVY}
							href={enrollStudentHref}
							data-class-id={classToAdd.classId}
							data-course-id={classToAdd.courseId}
						>
							SUBMIT
						</LinkButton>
					) : (
						<Button
							variant={ButtonVariant.NAVY}
							className="grow"
							disabled={true}
						>
							SUBMIT
						</Button>
					)}
				</div>
			</div>
		</div>
	);
};

export default EnrollModalBody;
